import { useState, useEffect, useMemo } from 'react'
import {
  Modal, Stack, TextInput, Select, Group, Button, PasswordInput, ActionIcon,
  Tooltip, Progress, Text, Alert, Badge,
} from '@mantine/core'
import { IconWand, IconCopy, IconCheck, IconAlertTriangle } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { formatApiError } from '@/api/errors'
import { rolesApi, type Role } from '@/api/roles'
import {
  permissionsApi,
  type PermissionTemplate,
} from '@/api/permissions'
import { permissionLabel, resourceLabel, buildResourceMetaIndex } from '@/api/permissionDisplay'
import { useAuthStore } from '@/store/auth'

interface UserCreateModalProps {
  opened: boolean
  onClose: () => void
  users: Array<{ email: string }>
}

function generatePassword(): string {
  const upper = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
  const lower = 'abcdefghjkmnpqrstuvwxyz'
  const digits = '23456789'
  const special = '!@#$%&*'
  const all = upper + lower + digits + special
  const arr = new Uint32Array(14)
  crypto.getRandomValues(arr)
  const guaranteed = [
    upper[arr[0] % upper.length],
    upper[arr[1] % upper.length],
    lower[arr[2] % lower.length],
    lower[arr[3] % lower.length],
    digits[arr[4] % digits.length],
    digits[arr[5] % digits.length],
    special[arr[6] % special.length],
  ]
  const rest = Array.from({ length: 7 }, (_, i) => all[arr[7 + i] % all.length])
  const combined = [...guaranteed, ...rest]
  const shuffle = new Uint32Array(combined.length)
  crypto.getRandomValues(shuffle)
  for (let i = combined.length - 1; i > 0; i--) {
    const j = shuffle[i] % (i + 1);
    [combined[i], combined[j]] = [combined[j], combined[i]]
  }
  return combined.join('')
}

function getPasswordStrength(pw: string) {
  if (!pw) return { score: 0, label: '', color: 'gray' }
  let score = 0
  if (pw.length >= 10) score++
  if (pw.length >= 14) score++
  if (/[A-Z]/.test(pw)) score++
  if (/[a-z]/.test(pw)) score++
  if (/[0-9]/.test(pw)) score++
  if (/[^A-Za-z0-9]/.test(pw)) score++
  if (score <= 2) return { score: Math.round((score / 6) * 100), label: 'Fraca', color: 'red' }
  if (score <= 4) return { score: Math.round((score / 6) * 100), label: 'Média', color: 'yellow' }
  return { score: Math.round((score / 6) * 100), label: 'Forte', color: 'teal' }
}

export function UserCreateModal({ opened, onClose, users }: UserCreateModalProps) {
  const { user, selectedTenantId } = useAuthStore()
  const ctxTenantId = selectedTenantId ?? user?.tenants?.[0]?.id ?? 1

  const qc = useQueryClient()
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [role, setRole] = useState<string>('developer')
  const [unixPassword, setUnixPassword] = useState('')
  const [pwCopied, setPwCopied] = useState(false)
  const [createTemplateId, setCreateTemplateId] = useState<string | null>(null)
  const [emailDuplicate, setEmailDuplicate] = useState(false)

  useEffect(() => {
    if (opened) {
      setUnixPassword(generatePassword())
      setPwCopied(false)
      setEmail('')
      setFullName('')
      setRole('developer')
      setCreateTemplateId(null)
      setEmailDuplicate(false)
    }
  }, [opened])

  const checkDuplicateEmail = (value: string) => {
    if (!value.trim()) { setEmailDuplicate(false); return }
    const exists = users.some(u => u.email.toLowerCase() === value.trim().toLowerCase())
    setEmailDuplicate(exists)
  }

  const { data: allRoles = [] } = useQuery<Role[]>({
    queryKey: ['roles'],
    queryFn: () => rolesApi.listRoles(),
    enabled: opened,
  })

  const { data: permTemplates = [] } = useQuery<PermissionTemplate[]>({
    queryKey: ['permission-templates'],
    queryFn: () => permissionsApi.listTemplates(),
    enabled: opened,
  })

  // Meta de menu dos resources, para o preview do perfil mostrar o rotulo
  // curado em vez da chave crua. Mesma queryKey do drawer e da tela de
  // Permissoes: o cache do TanStack serve as tres sem pedido extra.
  const { data: catalogo } = useQuery({
    queryKey: ['permissions-catalog'],
    queryFn: () => permissionsApi.listPermissions(),
    enabled: opened,
  })
  const resourceMeta = useMemo(
    () => buildResourceMetaIndex(catalogo?.resources ?? []),
    [catalogo],
  )

  const roleOptions = allRoles.map((r) => ({ value: r.code, label: r.name || r.code }))
  const pwStrength = getPasswordStrength(unixPassword)

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post(
        '/v1/users',
        {
          email,
          full_name: fullName,
          role_codes: [role],
          tenant_ids: (() => {
            const ids = createTemplateId
              ? (permTemplates.find((t) => String(t.id) === createTemplateId)?.tenants?.map((t) => t.id) ?? [])
              : (user?.tenants?.map((t) => t.id) ?? [])
            return ids.length > 0 ? ids : [ctxTenantId]
          })(),
          unix_password: unixPassword || undefined,
          template_id: createTemplateId ? Number(createTemplateId) : undefined,
        },
      )
      return res.data
    },
    onSuccess: async (data) => {
      if (createTemplateId && data?.id) {
        try {
          await permissionsApi.applyTemplate(data.id, Number(createTemplateId))
          const tmpl = permTemplates.find((t) => String(t.id) === createTemplateId)
          const tmplTenantNames = tmpl?.tenants?.map((t) => t.name).join(', ') || 'global'
          notifications.show({ color: 'teal', title: 'Perfil aplicado', message: `Perfil "${tmpl?.name}" aplicado em: ${tmplTenantNames}.` })
        } catch {
          notifications.show({ color: 'yellow', title: 'Aviso', message: 'Usuário criado mas falha ao aplicar template de permissões.' })
        }
      }
      qc.invalidateQueries({ queryKey: ['users'] })
      notifications.show({ color: 'teal', title: 'Usuário criado', message: `${email} criado no ConsoleAI e no Kanidm.` })
      onClose()
    },
    onError: (err: any) => {
      const raw = err?.response?.data?.detail
      const msg = typeof raw === 'string' && raw.startsWith('unix_credential_failed')
        ? 'Senha LDAP rejeitada pelo Kanidm (pode estar na badlist). Use o gerador para criar uma senha aleatória forte.'
        : formatApiError(err, 'Erro ao criar usuário.')
      notifications.show({ color: 'red', title: 'Erro', message: msg })
    },
  })

  const selectedTemplate = createTemplateId
    ? permTemplates.find((t) => String(t.id) === createTemplateId)
    : null

  return (
    <Modal opened={opened} onClose={onClose} title="Novo Usuário" centered>
      <Stack gap="md">
        <TextInput
          label="Email"
          placeholder="usuario@empresa.com"
          value={email}
          onChange={(e) => { setEmail(e.target.value); setEmailDuplicate(false) }}
          onBlur={(e) => checkDuplicateEmail(e.target.value)}
          error={emailDuplicate ? 'Email já cadastrado' : undefined}
          withAsterisk
          required
        />
        <TextInput
          label="Nome completo"
          placeholder="Nome do usuário"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          withAsterisk
          required
        />
        <Select
          label="Função inicial"
          value={role}
          onChange={(v) => setRole(v ?? 'developer')}
          data={roleOptions.length > 0 ? roleOptions : [
            { value: 'admin', label: 'Administrador' },
            { value: 'manager', label: 'Gerente' },
            { value: 'developer', label: 'Developer' },
            { value: 'suporte', label: 'Suporte' },
            { value: 'viewer', label: 'Viewer' },
          ]}
        />
        <Select
          label="Perfil de permissões"
          description="Opcional — aplica permissões automaticamente ao criar"
          placeholder="Nenhum template"
          data={permTemplates.map((t) => ({ value: String(t.id), label: `${t.name} — ${t.description}` }))}
          value={createTemplateId}
          onChange={setCreateTemplateId}
          searchable
          nothingFoundMessage="Nenhum template encontrado"
          clearable
        />
        {selectedTemplate && (
          <Group gap={4}>
            {selectedTemplate.permissions.map((p) => (
              <Badge key={p.id} size="xs" variant="light" color="violet">
                {resourceLabel(p.resource, resourceMeta.get(p.resource))}: {permissionLabel(p)}
              </Badge>
            ))}
          </Group>
        )}
        <Stack gap={4}>
          <Group gap="xs" align="flex-end">
            <PasswordInput
              label="Senha de acesso ao ConsoleAI (LDAP)"
              placeholder="Gerada automaticamente — edite se desejar"
              description="Gerada automaticamente. Copie e envie ao usuário."
              value={unixPassword}
              onChange={(e) => setUnixPassword(e.target.value)}
              style={{ flex: 1 }}
              data-sensitive="true"
              wrapperProps={{ 'data-sensitive': 'true' }}
            />
            <Tooltip label="Gerar senha segura">
              <ActionIcon
                variant="light"
                size="lg"
                mb={1}
                onClick={() => { setUnixPassword(generatePassword()); setPwCopied(false) }}
              >
                <IconWand size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label={pwCopied ? 'Copiado!' : 'Copiar senha'}>
              <ActionIcon
                variant="light"
                size="lg"
                mb={1}
                disabled={!unixPassword}
                color={pwCopied ? 'teal' : 'blue'}
                onClick={() => {
                  if (!unixPassword) return
                  navigator.clipboard.writeText(unixPassword)
                  setPwCopied(true)
                  setTimeout(() => setPwCopied(false), 2000)
                }}
              >
                {pwCopied ? <IconCheck size={16} /> : <IconCopy size={16} />}
              </ActionIcon>
            </Tooltip>
          </Group>
          {unixPassword && (
            <Group gap="xs" align="center" mt={2}>
              <Progress value={pwStrength.score} color={pwStrength.color} size="xs" style={{ flex: 1 }} />
              <Text size="xs" c={pwStrength.color} w={40}>{pwStrength.label}</Text>
            </Group>
          )}
        </Stack>
        <Alert icon={<IconAlertTriangle size={14} />} color="blue">
          O usuário será criado no ConsoleAI e um e-mail com a senha temporária será enviado automaticamente. Após a confirmação do cadastro, o usuário será provisionado no Kanidm.
        </Alert>
        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose}>Cancelar</Button>
          <Button
            loading={createMutation.isPending}
            onClick={() => createMutation.mutate()}
            disabled={!email || !fullName || emailDuplicate}
          >
            Criar
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
